/**
 * Crossword — daily-seed selection.
 *
 * Mirrors `pickDailyWord` in `games/five/words`. The convention is fixed by
 * CONTRACT.md:
 *
 *   today's puzzle for a size = fnv1a("YYYYMMDD" + size) % puzzlesOfSize(size).length
 *
 * so everyone in a community lands on the same Mini and the same Midi on a
 * given day, and an "Another one" affordance reseeds for unlimited practice.
 *
 * The default date is "now" in local time, so the puzzle rolls over at local
 * midnight — matching how residents experience days.
 */
import type { CrosswordPuzzle, CrosswordSize } from '@rendever/puzzle-core/crossword';
import { ALL_PUZZLES, puzzlesOfSize } from '@rendever/puzzle-content/crossword';

/** YYYYMMDD in local time. */
export function dateKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

/**
 * FNV-1a hash → unsigned 32-bit. Not cryptographic; just needs even spread.
 * Matches the hash family used by `games/five/words.dateSeed`.
 */
export function fnv1a(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * The daily puzzle for a given size, or `null` if the corpus has none of that
 * size yet. Same date + size → same puzzle, every time.
 */
export function pickDailyPuzzle(
  size: CrosswordSize,
  date: Date = new Date(),
): CrosswordPuzzle | null {
  const pool = puzzlesOfSize(size);
  if (pool.length === 0) return null;
  const idx = fnv1a(dateKey(date) + size) % pool.length;
  return pool[idx] ?? null;
}

/**
 * A fresh reseeded pick for the same size — the "Another one" affordance.
 * Avoids repeating `exclude` when the pool has more than one puzzle.
 */
export function pickAnotherPuzzle(
  size: CrosswordSize,
  exclude?: string,
  rng: () => number = Math.random,
): CrosswordPuzzle | null {
  const pool = puzzlesOfSize(size);
  if (pool.length === 0) return null;
  // Pick from the puzzles OTHER than the excluded one, so we never repeat it
  // regardless of how the rng falls. Fall back to the full pool only when
  // excluding leaves nothing (the pool is just the excluded puzzle).
  const others = exclude ? pool.filter((p) => p.id !== exclude) : pool;
  const from = others.length > 0 ? others : pool;
  return from[Math.floor(rng() * from.length)] ?? null;
}

/** Look up a specific puzzle by id (used to resume a deep link). */
export function puzzleById(id: string): CrosswordPuzzle | null {
  return ALL_PUZZLES.find((p) => p.id === id) ?? null;
}

/**
 * The sizes the hub leads with, in order. Mini and Midi map to their schema
 * tokens directly; `'full'` is our full-size tier — an 11×11 grid (we do NOT
 * ship 15×15). Each is daily-seeded the same way via `pickDailyPuzzle`, so a
 * community lands on the same Full Crossword each day, just like the Mini.
 */
export const HUB_SIZES: ReadonlyArray<CrosswordSize> = ['mini', 'midi', 'full'];
